import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  // useTheme/useMediaQuery остаются для других мест, но isMobile берём из общего хука
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import { logout } from '../../store/slices/authSlice';
import { getMenuItems } from '../../utils/menuConfig';
import { useIsMobile } from '../../utils/responsive';
import { APP_LOGO_SRC, APP_NAME } from '../../utils/brand';

export default function AppShell({ children }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  
  const { user } = useSelector((state) => state.auth);
  const menuItems = useMemo(() => getMenuItems(user), [user]);
  
  // State
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [submenuItems, setSubmenuItems] = useState([]);
  const [mobileSubmenuAnchor, setMobileSubmenuAnchor] = useState(null);
  const [mobileSubmenuItems, setMobileSubmenuItems] = useState([]);
  
  // Находим текущий активный таб.
  // Сначала матчим по дочерним пунктам (точный путь), затем — по наиболее
  // специфичному (самому длинному) item.path, чтобы префикс «/analytics»
  // не перебивал «/analytics/supply-plan» и т.п.
  const getCurrentTab = () => {
    const path = location.pathname;

    // 1) Точное совпадение с каким-либо дочерним пунктом
    const byChild = menuItems.findIndex((item) =>
      item.children?.some(
        (child) => path === child.path || path.startsWith(child.path + '/')
      )
    );
    if (byChild >= 0) return byChild;

    // 2) Наиболее специфичное совпадение по item.path
    let bestIndex = -1;
    let bestLen = -1;
    menuItems.forEach((item, index) => {
      if (path === item.path || path.startsWith(item.path + '/')) {
        if (item.path.length > bestLen) {
          bestLen = item.path.length;
          bestIndex = index;
        }
      }
    });
    return bestIndex >= 0 ? bestIndex : 0;
  };
  
  // Desktop tab click
  const handleTabClick = (event, item, index) => {
    if (item.children && item.children.length > 0) {
      // Открываем подменю
      setSubmenuAnchor(event.currentTarget);
      setSubmenuItems(item.children);
    } else {
      navigate(item.path);
    }
  };
  
  // Mobile bottom nav click
  const handleMobileNavChange = (event, newValue) => {
    const item = menuItems[newValue];
    if (item) {
      if (item.children && item.children.length > 0) {
        // Открываем подменю
        setMobileSubmenuAnchor(event.currentTarget);
        setMobileSubmenuItems(item.children);
      } else {
        navigate(item.path);
      }
    }
  };
  
  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setSubmenuItems([]);
  };
  
  const handleMobileSubmenuClose = () => {
    setMobileSubmenuAnchor(null);
    setMobileSubmenuItems([]);
  };
  
  const handleSubmenuItemClick = (path) => {
    navigate(path);
    handleSubmenuClose();
    handleMobileSubmenuClose();
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleLogout = () => {
    handleCloseUserMenu();
    dispatch(logout());
    navigate('/login');
  };

  // Рендер пунктов подменю с заголовками разделов (если у пунктов задан group)
  const renderSubmenuItems = (items) => {
    const nodes = [];
    let lastGroup = null;
    items.forEach((item) => {
      if (item.group && item.group !== lastGroup) {
        lastGroup = item.group;
        nodes.push(
          <ListSubheader
            key={`group-${item.group}`}
            disableSticky
            sx={{
              lineHeight: '32px',
              fontSize: '0.72rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'text.secondary',
              bgcolor: 'transparent',
            }}
          >
            {item.group}
          </ListSubheader>
        );
      }
      nodes.push(
        <MenuItem
          key={item.path}
          onClick={() => handleSubmenuItemClick(item.path)}
          selected={location.pathname === item.path}
          sx={item.group ? { pl: 3 } : undefined}
        >
          <ListItemText>{item.text}</ListItemText>
        </MenuItem>
      );
    });
    return nodes;
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', overflowX: 'hidden' }}>
      {/* AppBar */}
      <AppBar position="fixed">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo */}
            <Box
              component="img"
              src={APP_LOGO_SRC}
              alt={APP_NAME}
              sx={{
                mr: 1,
                height: 32,
                width: 'auto',
                display: 'block',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                mr: 4,
                fontWeight: 500,
                color: '#47257D',
                fontSize: { xs: '1.35rem', sm: '1.5rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              {APP_NAME}
            </Typography>
            
            {/* Desktop Tabs */}
            {!isMobile && (
              <Tabs
                value={getCurrentTab()}
                textColor="inherit"
                indicatorColor="secondary"
                sx={{
                  flexGrow: 1,
                  '& .MuiTab-root': {
                    minHeight: 64,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <Tab
                    key={item.path}
                    label={item.text}
                    onClick={(e) => handleTabClick(e, item, index)}
                  />
                ))}
              </Tabs>
            )}
            
            {/* Right side */}
            <Box sx={{ flexGrow: isMobile ? 1 : 0 }} />
            
            {/* Notifications */}
            <Tooltip title="Уведомления">
              <IconButton color="inherit" sx={{ mr: 1 }}>
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            {/* User menu */}
            <Tooltip title="Профиль">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  {user?.firstName?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            
            <Menu
              sx={{ mt: '45px' }}
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle1">
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/profile'); }}>
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                Профиль
              </MenuItem>
              <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/settings'); }}>
                <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                Настройки
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                Выйти
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Desktop Submenu */}
      <Menu
        anchorEl={submenuAnchor}
        open={Boolean(submenuAnchor)}
        onClose={handleSubmenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {renderSubmenuItems(submenuItems)}
      </Menu>
      
      {/* Mobile Submenu */}
      <Menu
        anchorEl={mobileSubmenuAnchor}
        open={Boolean(mobileSubmenuAnchor)}
        onClose={handleMobileSubmenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {renderSubmenuItems(mobileSubmenuItems)}
      </Menu>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 8, // Toolbar height
          pb: isMobile ? 10 : 3, // Space for bottom nav on mobile
          px: { xs: 2, sm: 3 },
          bgcolor: 'background.default',
          overflowX: 'hidden',
          minWidth: 0,
        }}
      >
        <Container maxWidth="xl" sx={{ minWidth: 0, overflowX: 'hidden' }}>
          {children}
        </Container>
      </Box>
      
      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            overflow: 'hidden',
          }}
          elevation={3}
        >
          <BottomNavigation
            value={getCurrentTab()}
            onChange={handleMobileNavChange}
            showLabels={menuItems.length <= 6}
            sx={{
              width: '100%',
              height: 64,
              overflow: 'hidden',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                flex: 1,
                maxWidth: 'none',
                px: 0.25,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.625rem',
                lineHeight: 1.15,
                whiteSpace: 'normal',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
                mt: 0.25,
                '&.Mui-selected': {
                  fontSize: '0.625rem',
                },
              },
            }}
          >
            {menuItems.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={item.shortLabel || item.text}
                icon={item.icon}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
